import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Gift,
  Star,
  Crown,
  Award,
  Clock,
  Check,
  X,
  ShoppingCart,
  History,
  Sparkles,
  AlertTriangle
} from "lucide-react";

const REWARD_ICONS = {
  "star": Star,
  "crown": Crown,
  "award": Award,
  "gift": Gift
};

const RewardsCatalog = () => {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState([]);
  const [currentPoints, setCurrentPoints] = useState(0);
  const [history, setHistory] = useState([]);
  const [activeRewards, setActiveRewards] = useState([]);
  const [selectedReward, setSelectedReward] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [activeTab, setActiveTab] = useState("catalog");

  useEffect(() => {
    fetchCatalog();
    fetchHistory();
    fetchActiveRewards();
  }, []);

  const fetchCatalog = async () => {
    try {
      const response = await fetch(`${API}/referral/rewards/catalog`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCatalog(data.rewards || []);
        setCurrentPoints(data.current_points || 0);
      }
    } catch (error) {
      console.error("Failed to fetch catalog:", error);
      toast.error("Failed to load rewards catalog");
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await fetch(`${API}/referral/rewards/history`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.redemptions || []);
      }
    } catch (error) {
      console.error("Failed to fetch history:", error);
    }
  };

  const fetchActiveRewards = async () => {
    try {
      const response = await fetch(`${API}/referral/rewards/active`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setActiveRewards([
          ...(data.active_subscriptions || []),
          ...(data.permanent_rewards || [])
        ]);
      }
    } catch (error) {
      console.error("Failed to fetch active rewards:", error);
    }
  };

  const handleRedeem = async () => {
    if (!selectedReward) return;
    
    setRedeeming(true);
    try {
      const response = await fetch(`${API}/referral/rewards/redeem/${selectedReward.id}`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setSelectedReward(null);
        setCurrentPoints(data.remaining_points);
        
        // Refresh data
        fetchCatalog();
        fetchHistory();
        fetchActiveRewards();
        
        // Refresh user context if subscription was upgraded
        if (selectedReward.category === "subscription") {
          await checkAuth();
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to redeem reward");
      }
    } catch (error) {
      toast.error("Failed to redeem reward");
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex">
        <Sidebar />
        <main className="flex-1 ml-64 p-8">
          <div className="flex items-center justify-center h-64">
            <div className="w-16 h-16 bg-crimson/20 flex items-center justify-center animate-pulse">
              <Gift className="w-8 h-8 text-crimson" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian flex">
      <Sidebar />
      <main className="flex-1 ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-3xl font-bold mb-2">
              REWARDS <span className="text-crimson">CATALOG</span>
            </h1>
            <p className="text-gray-400">
              Redeem your referral points for exclusive rewards
            </p>
          </div>
          <div className="bg-obsidian-light border border-crimson/30 px-6 py-3" data-testid="points-balance">
            <p className="text-gray-400 text-sm">Your Points</p>
            <p className="text-3xl font-bold text-crimson">{currentPoints}</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab("catalog")}
            className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === "catalog"
                ? "bg-crimson text-white"
                : "bg-obsidian-light border border-[#333] text-gray-400 hover:border-crimson"
            }`}
            data-testid="tab-catalog"
          >
            <ShoppingCart className="w-4 h-4" />
            Catalog
          </button>
          <button
            onClick={() => setActiveTab("active")}
            className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === "active"
                ? "bg-crimson text-white"
                : "bg-obsidian-light border border-[#333] text-gray-400 hover:border-crimson"
            }`}
            data-testid="tab-active"
          >
            <Sparkles className="w-4 h-4" />
            Active ({activeRewards.length})
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
              activeTab === "history"
                ? "bg-crimson text-white"
                : "bg-obsidian-light border border-[#333] text-gray-400 hover:border-crimson"
            }`}
            data-testid="tab-history"
          >
            <History className="w-4 h-4" />
            History ({history.length})
          </button>
        </div>

        {/* Catalog Tab */}
        {activeTab === "catalog" && (
          <div className="grid grid-cols-3 gap-6">
            {catalog.map((reward) => {
              const IconComponent = REWARD_ICONS[reward.icon] || Gift;
              const isDisabled = !reward.available;
              
              return (
                <motion.div
                  key={reward.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`bg-obsidian-light border p-6 relative ${
                    isDisabled 
                      ? "border-[#222] opacity-60" 
                      : "border-[#333] hover:border-crimson cursor-pointer"
                  }`}
                  onClick={() => !isDisabled && setSelectedReward(reward)}
                  data-testid={`reward-card-${reward.id}`}
                >
                  {reward.already_redeemed && (
                    <div className="absolute top-3 right-3 bg-green-500/20 text-green-400 px-2 py-1 text-xs">
                      OWNED
                    </div>
                  )}
                  
                  <div className={`w-14 h-14 flex items-center justify-center mb-4 ${
                    reward.category === "subscription" 
                      ? "bg-blue-500/20" 
                      : "bg-yellow-500/20"
                  }`}>
                    <IconComponent className={`w-7 h-7 ${
                      reward.category === "subscription" 
                        ? "text-blue-400" 
                        : "text-yellow-400"
                    }`} />
                  </div>
                  
                  <h3 className="font-bold text-lg text-white mb-2">{reward.name}</h3>
                  <p className="text-gray-500 text-sm mb-4">{reward.description}</p>
                  
                  {reward.duration_days && (
                    <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
                      <Clock className="w-4 h-4" />
                      {reward.duration_days} days
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#222]">
                    <span className={`text-lg font-bold ${
                      reward.can_afford ? "text-crimson" : "text-gray-500"
                    }`}>
                      {reward.points_cost} pts
                    </span>
                    {reward.available && (
                      <Button
                        size="sm"
                        className="bg-crimson hover:bg-crimson-hover"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedReward(reward);
                        }}
                        data-testid={`redeem-btn-${reward.id}`}
                      >
                        Redeem
                      </Button>
                    )}
                    {!reward.can_afford && !reward.already_redeemed && (
                      <span className="text-gray-500 text-sm">
                        Need {reward.points_cost - currentPoints} more
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Active Rewards Tab */}
        {activeTab === "active" && (
          <div className="space-y-4">
            {activeRewards.length === 0 ? (
              <div className="bg-obsidian-light border border-[#222] p-12 text-center">
                <Gift className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No active rewards yet</p>
                <p className="text-gray-600 text-sm">Redeem your points in the catalog!</p>
              </div>
            ) : (
              activeRewards.map((reward, idx) => (
                <motion.div
                  key={reward.id || idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-obsidian-light border border-[#222] p-6 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 flex items-center justify-center ${
                      reward.category === "subscription" 
                        ? "bg-green-500/20" 
                        : "bg-yellow-500/20"
                    }`}>
                      {reward.category === "subscription" ? (
                        <Star className="w-6 h-6 text-green-400" />
                      ) : (
                        <Award className="w-6 h-6 text-yellow-400" />
                      )}
                    </div>
                    <div>
                      <h4 className="font-bold text-white">{reward.reward_name}</h4>
                      <p className="text-gray-500 text-sm">
                        Redeemed on {new Date(reward.redeemed_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  {reward.days_remaining !== null && reward.days_remaining !== undefined ? (
                    <div className="text-right">
                      <p className={`font-bold ${
                        reward.days_remaining > 7 ? "text-green-400" : 
                        reward.days_remaining > 0 ? "text-yellow-400" : "text-red-400"
                      }`}>
                        {reward.days_remaining > 0 ? `${reward.days_remaining} days left` : "Expired"}
                      </p>
                      <p className="text-gray-500 text-xs">
                        Expires {new Date(reward.expires_at).toLocaleDateString()}
                      </p>
                    </div>
                  ) : (
                    <span className="bg-yellow-500/20 text-yellow-400 px-3 py-1 text-sm">
                      PERMANENT
                    </span>
                  )}
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === "history" && (
          <div className="space-y-4">
            {history.length === 0 ? (
              <div className="bg-obsidian-light border border-[#222] p-12 text-center">
                <History className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                <p className="text-gray-400">No redemption history yet</p>
              </div>
            ) : (
              history.map((item, idx) => (
                <motion.div
                  key={item.id || idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="bg-obsidian-light border border-[#222] p-4 flex items-center justify-between"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                      <Gift className="w-5 h-5 text-crimson" />
                    </div>
                    <div>
                      <h4 className="font-medium text-white">{item.reward_name}</h4>
                      <p className="text-gray-500 text-sm">
                        {new Date(item.redeemed_at).toLocaleDateString()} at{" "}
                        {new Date(item.redeemed_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-crimson font-bold">-{item.points_spent} pts</p>
                    <p className={`text-xs ${item.is_active ? "text-green-400" : "text-gray-500"}`}>
                      {item.is_active ? "Active" : item.category === "badge" ? "Permanent" : "Expired"}
                    </p>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* Confirmation Modal */}
        <AnimatePresence>
          {selectedReward && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
              onClick={() => setSelectedReward(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-obsidian-light border border-[#333] p-8 max-w-md w-full mx-4"
                data-testid="redeem-confirmation-modal"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading text-xl font-bold">
                    Confirm <span className="text-crimson">Redemption</span>
                  </h3>
                  <button 
                    onClick={() => setSelectedReward(null)}
                    className="p-1 hover:bg-[#222]"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>

                <div className="bg-[#111] border border-[#222] p-6 mb-6 text-center">
                  {(() => {
                    const Icon = REWARD_ICONS[selectedReward.icon] || Gift;
                    return (
                      <div className="w-16 h-16 mx-auto mb-4 bg-crimson/20 flex items-center justify-center">
                        <Icon className="w-8 h-8 text-crimson" />
                      </div>
                    );
                  })()}
                  <h4 className="font-bold text-lg text-white mb-2">{selectedReward.name}</h4>
                  <p className="text-gray-500 text-sm mb-4">{selectedReward.description}</p>
                  {selectedReward.duration_days && (
                    <p className="text-gray-400 text-sm">
                      Duration: {selectedReward.duration_days} days
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between mb-6 p-4 bg-[#111]">
                  <span className="text-gray-400">Cost</span>
                  <span className="text-crimson font-bold text-xl">{selectedReward.points_cost} points</span>
                </div>

                <div className="flex items-center justify-between mb-6 p-4 bg-[#111]">
                  <span className="text-gray-400">After Redemption</span>
                  <span className="text-white font-bold">
                    {currentPoints - selectedReward.points_cost} points remaining
                  </span>
                </div>

                {selectedReward.category === "subscription" && (
                  <div className="bg-green-500/10 border border-green-500/30 p-4 mb-6">
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
                      <p className="text-green-400 text-sm">
                        Your subscription will be upgraded immediately!
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex gap-4">
                  <Button
                    variant="outline"
                    className="flex-1 border-[#333] text-white hover:bg-[#222]"
                    onClick={() => setSelectedReward(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    className="flex-1 bg-crimson hover:bg-crimson-hover"
                    onClick={handleRedeem}
                    disabled={redeeming}
                    data-testid="confirm-redeem-btn"
                  >
                    {redeeming ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Confirm Redemption
                      </>
                    )}
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default RewardsCatalog;
